"use client"

import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"
import { controlRadiusClass } from "@/components/ui/field-surface"

/** Square control styled like brand progress checkmarks (signal / primary), not native OS accent. */
type CheckboxProps = Omit<
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  "onCheckedChange"
> & {
  onCheckedChange?: (checked: boolean) => void
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, onCheckedChange, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer size-4 shrink-0 border-2 border-muted-foreground/40 bg-transparent shadow-none outline-none transition-[color,box-shadow,border-color,background-color]",
      controlRadiusClass,
      "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:border-primary data-[state=checked]:bg-transparent",
      className,
    )}
    data-slot="checkbox"
    data-component="checkbox"
    onCheckedChange={(value) => {
      if (value === "indeterminate") return
      onCheckedChange?.(value)
    }}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex size-full items-center justify-center text-primary">
      <Check
        className="size-3.5"
        strokeWidth={2.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))

Checkbox.displayName = CheckboxPrimitive.Root.displayName ?? "Checkbox"

export { Checkbox }
export type { CheckboxProps }
